import React, { useState, useEffect } from 'react';
import '../../styles/PosterGenerator.css';
import { BACKEND_URL } from "../../config";
import EditorCanvas from "./EditorCanvas";
import TemplateList from "./TemplateList";
import axios from "axios";

const PosterGenerator = () => {
    const [templateId, setTemplateId] = useState('template1'); // Default selected template
    const [description, setDescription] = useState('');
    const [genAIModel, setGenAIModel] = useState('flux_schnell'); // Default GenAI model
    const [posterData, setPosterData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [templates, setTemplates] = useState([]); // State for fetched templates
    const [templatesLoading, setTemplatesLoading] = useState(true); // Separate loading state for templates
    const [isAdvancedOpen, setIsAdvancedOpen] = useState(false); // State to toggle advanced options

    // Fetch templates when the component mounts
    useEffect(() => {
        const fetchTemplates = async () => {
            setTemplatesLoading(true);
            try {
                const response = await axios.get(`${BACKEND_URL}v2/posters/templates`);
                setTemplates(response.data); // Assuming the response is an array of {id, name, preview}
                setTemplatesLoading(false);
                console.log("Fetched templates: ", response.data);
            } catch (error) {
                setErrorMessage(error.response?.data?.detail || 'Failed to load templates');
                setTemplatesLoading(false);
            }
        };
        fetchTemplates();
    }, []); // Empty dependency array means it runs once on mount

    const handleTemplateSelect = (id) => {
        setTemplateId(id);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!templateId || !description) {
            setErrorMessage('Please select a template and fill in the description');
            return;
        }

        setLoading(true);
        setErrorMessage('');

        try {
            const response = await fetch(`${BACKEND_URL}v2/posters/generate-poster`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    template_id: templateId,
                    poster_description: description,
                    provider_name: genAIModel,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to generate poster');
            }

            const data = await response.json();
            console.log("Data:", data);
            setPosterData(data);
        } catch (error) {
            setErrorMessage(error.message || 'An error occurred while generating the poster');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="poster-generator">
            <div className="container">
                <div className="form-container">
                    <h1 className="form-title">Design Your Poster</h1>

                    <form onSubmit={handleSubmit} className="form">
                        {errorMessage && (
                            <div className="error-message">{errorMessage}</div>
                        )}

                        <div className="form-group">
                            <label className="form-label" htmlFor="description">Poster Description</label>
                            <textarea
                                id="description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="form-input textarea"
                                placeholder="Describe your poster vision..."
                                disabled={loading || templatesLoading}
                            />
                            <div className={`advanced-options ${isAdvancedOpen ? 'open' : ''}`}>
                                <div
                                    className={`advanced-options-header ${isAdvancedOpen ? 'open' : ''}`}
                                    onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                                >
                                    <span className="triangle-icon"></span>
                                    <span>Advanced Options</span>
                                </div>
                                <div className={`advanced-options-content ${isAdvancedOpen ? 'open' : ''}`}>
                                    <div className="genai-model-selector">
                                        <label className="form-label" htmlFor="genai-model">GenAI Model</label>
                                        <select
                                            id="genai-model"
                                            value={genAIModel}
                                            onChange={(e) => setGenAIModel(e.target.value)}
                                            className="form-input"
                                            disabled={loading || templatesLoading}
                                        >
                                            <option value="ideogram">Ideogram</option>
                                            <option value="stable_diffusion_xl">Stable Diffusion XL</option>
                                            <option value="flux_schnell">FLUX.1</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Select Template</label>
                            {templatesLoading ? (
                                <div>Loading templates...</div>
                            ) : (
                                <TemplateList
                                    templates={templates}
                                    selectedTemplate={templateId}
                                    onTemplateSelect={handleTemplateSelect}
                                />
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={loading || templatesLoading}
                            className="submit-btn"
                        >
                            {loading ? 'Generating...' : 'Create Poster'}
                        </button>
                    </form>
                </div>

                {posterData && (
                    <EditorCanvas composedPoster={posterData} />
                )}

                {loading && (
                    <div className="loading-overlay">
                        <div className="loading-spinner"></div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PosterGenerator;